import apiClient from './apiClient';

/**
 * JavaScript Printing Manager
 * 
 * A reliable alternative to WebUSB for thermal label printing.
 * Provides multiple printing methods:
 * 1. Browser Print - Native browser print dialog
 * 2. PDF Download - Generate PDF labels for printing
 * 3. Raw Download - Download .prn/.zpl files for direct printer commands
 * 4. Print Server - HTTP-based printing for advanced setups
 * 
 * Usage:
 *   import { PrintManager } from '@/services/printManager';
 *   
 *   // Print labels using browser dialog
 *   await PrintManager.printLabels(codes, { method: 'browser' });
 *   
 *   // Generate PDF for printing
 *   await PrintManager.printLabels(codes, { method: 'pdf' });
 */

// ==========================================
// Configuration
// ==========================================

const DEFAULT_CONFIG = {
  labelWidth: 33,       // mm
  labelHeight: 26,      // mm
  dpi: 203,            // printer DPI
  copies: 1,
  language: 'tspl',    // tspl, zpl, escpos
  fontSize: 10,
  qrSize: 80,          // pixels
  showBorder: false,
};

// ==========================================
// Print Manager Class
// ==========================================

class PrintManagerClass {
  constructor() {
    this.config = { ...DEFAULT_CONFIG };
    this.printWindow = null;
    this.printServerUrl = null;
  }

  /**
   * Configure the print manager
   */
  configure(options) {
    this.config = { ...this.config, ...options };
    return this;
  }

  /**
   * Set print server URL for HTTP-based printing
   */
  setPrintServer(url) {
    this.printServerUrl = url;
    return this;
  }

  /**
   * Main print method - automatically selects best available method
   */
  async printLabels(codes, options = {}) {
    const method = options.method || 'browser';
    const config = { ...this.config, ...options };

    if (!codes || codes.length === 0) {
      throw new Error('No codes provided for printing');
    }

    // Normalize codes to array of objects
    const normalizedCodes = this._normalizeCodes(codes);

    switch (method) {
      case 'browser':
        return this.printViaBrowser(normalizedCodes, config);
      case 'pdf':
        return this.printViaPDF(normalizedCodes, config);
      case 'raw':
        return this.downloadRawCommands(normalizedCodes, config);
      case 'server':
        return this.printViaPrintServer(normalizedCodes, config);
      default:
        return this.printViaBrowser(normalizedCodes, config);
    }
  }

  /**
   * Browser Print Method
   * Opens a new window with formatted labels and triggers print dialog
   */
  async printViaBrowser(codes, config = {}) {
    const { copies = 1, showBorder = false, labelWidth, labelHeight } = { ...this.config, ...config };
    
    // Generate QR code images from backend
    const labelsHtml = await this._generateLabelsHtml(codes, config);
    
    // Create print window
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      throw new Error('Popup blocked. Please allow popups for this site to print labels.');
    }

    // Write print document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Labels</title>
        <style>
          @page {
            size: ${labelWidth}mm ${labelHeight}mm;
            margin: 0;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .label {
            width: ${labelWidth}mm;
            height: ${labelHeight}mm;
            padding: 1mm;
            page-break-after: always;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            ${showBorder ? 'border: 0.5px solid #ccc;' : ''}
          }
          
          .label:last-child {
            page-break-after: avoid;
          }
          
          .qr-code {
            width: 18mm;
            height: 18mm;
          }
          
          .code-text {
            font-size: 7pt;
            font-weight: bold;
            text-align: center;
            margin-top: 1mm;
            word-break: break-all;
            max-width: 100%;
          }
          
          .sort-text {
            font-size: 6pt;
            color: #333;
            text-align: center;
          }
          
          @media print {
            body {
              background: white;
            }
            .no-print {
              display: none !important;
            }
          }
          
          @media screen {
            body {
              background: #f0f0f0;
              padding: 20px;
            }
            .label {
              background: white;
              margin: 10px auto;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              border: 1px solid #ddd;
            }
            .controls {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              background: #333;
              color: white;
              padding: 15px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              z-index: 1000;
            }
            .controls button {
              background: #4CAF50;
              color: white;
              border: none;
              padding: 10px 20px;
              font-size: 16px;
              cursor: pointer;
              border-radius: 4px;
            }
            .controls button:hover {
              background: #45a049;
            }
            .print-area {
              margin-top: 70px;
            }
          }
        </style>
      </head>
      <body>
        <div class="controls no-print">
          <span>📄 ${codes.length * copies} Label(s) Ready</span>
          <div>
            <button onclick="window.print()">🖨️ Print Labels</button>
            <button onclick="window.close()" style="background:#666;margin-left:10px;">✕ Close</button>
          </div>
        </div>
        <div class="print-area">
          ${labelsHtml}
        </div>
        <script>
          // Auto-focus on print when ready
          window.onload = function() {
            // Small delay to ensure images are loaded
            setTimeout(function() {
              window.focus();
            }, 500);
          };
        </script>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    this.printWindow = printWindow;
    
    return {
      success: true,
      method: 'browser',
      labelCount: codes.length,
      copies,
      message: 'Print window opened. Click "Print Labels" to print.'
    };
  }

  /**
   * PDF Print Method
   * Generates a PDF file with labels for download/print
   */
  async printViaPDF(codes, config = {}) {
    const { copies = 1 } = { ...this.config, ...config };
    
    try {
      const response = await apiClient.post('/printer/generate-pdf', {
        codes: codes.map(c => typeof c === 'string' ? { code: c } : c),
        copies,
        label_width_mm: config.labelWidth || this.config.labelWidth,
        label_height_mm: config.labelHeight || this.config.labelHeight
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const filename = `labels_${codes.length}_${Date.now()}.pdf`;
      
      // Option 1: Auto-download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Option 2: Open in new tab for print preview
      window.open(url, '_blank');
      
      // Cleanup after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      
      return {
        success: true,
        method: 'pdf',
        labelCount: codes.length,
        copies,
        filename,
        message: 'PDF generated and downloaded. Open and print from your PDF viewer.'
      };
    } catch (error) {
      // Fallback to browser print if PDF generation fails
      console.warn('PDF generation failed, falling back to browser print:', error);
      return this.printViaBrowser(codes, config);
    }
  }

  /**
   * Raw Commands Download
   * Downloads .prn/.zpl file for direct printer commands
   */
  async downloadRawCommands(codes, config = {}) {
    const { language = 'tspl', copies = 1 } = { ...this.config, ...config };
    
    const response = await apiClient.post('/printer/generate-commands', {
      codes: codes.map(c => typeof c === 'string' ? c : c.code),
      language,
      copies,
      label_width_mm: config.labelWidth || this.config.labelWidth,
      label_height_mm: config.labelHeight || this.config.labelHeight
    });
    
    // Create downloadable file
    const extMap = { zpl: 'zpl', tspl: 'prn', escpos: 'bin' };
    const ext = extMap[language] || 'txt';
    const filename = `labels_${codes.length}.${ext}`;
    
    const blob = new Blob([response.data.commands], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return {
      success: true,
      method: 'raw',
      labelCount: codes.length,
      copies,
      filename,
      language,
      message: `Downloaded ${filename}. Send to printer using command: copy /b ${filename} LPT1`
    };
  }

  /**
   * Print Server Method
   * Sends print job to a local print server via HTTP
   */
  async printViaPrintServer(codes, config = {}) {
    if (!this.printServerUrl) {
      throw new Error('Print server URL not configured. Call setPrintServer(url) first.');
    }
    
    const { language = 'tspl', copies = 1 } = { ...this.config, ...config };
    
    // Generate commands
    const response = await apiClient.post('/printer/generate-commands', {
      codes: codes.map(c => typeof c === 'string' ? c : c.code),
      language,
      copies,
      label_width_mm: config.labelWidth || this.config.labelWidth,
      label_height_mm: config.labelHeight || this.config.labelHeight
    });
    
    // Send to print server
    const printResponse = await fetch(this.printServerUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        commands: response.data.commands,
        language,
        copies
      })
    });
    
    if (!printResponse.ok) {
      throw new Error(`Print server error: ${printResponse.statusText}`);
    }
    
    return {
      success: true,
      method: 'server',
      labelCount: codes.length,
      copies,
      message: 'Print job sent to print server successfully.'
    };
  }

  /**
   * Quick print - single code
   */
  async printSingleLabel(code, options = {}) {
    return this.printLabels([code], options);
  }

  /**
   * Batch print with progress callback
   */
  async printBatch(codes, options = {}, onProgress = null) {
    const batchSize = options.batchSize || 50;
    const results = [];
    
    for (let i = 0; i < codes.length; i += batchSize) {
      const batch = codes.slice(i, i + batchSize);
      const result = await this.printLabels(batch, options);
      results.push(result);
      
      if (onProgress) {
        onProgress({
          completed: Math.min(i + batchSize, codes.length),
          total: codes.length,
          percentage: Math.round(((i + batchSize) / codes.length) * 100)
        });
      }
    }
    
    return {
      success: true,
      batches: results.length,
      totalLabels: codes.length,
      results
    };
  }

  // ==========================================
  // Private Helper Methods
  // ==========================================

  _normalizeCodes(codes) {
    return codes.map(code => {
      if (typeof code === 'string') {
        return { code, sortNo: null, description: null };
      }
      return {
        code: code.code || code.usn || code.barcode,
        sortNo: code.sortNo || code.sort_no || null,
        description: code.description || code.desc || null
      };
    });
  }

  async _generateLabelsHtml(codes, config) {
    const { copies = 1 } = config;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    let html = '';
    
    for (const codeObj of codes) {
      const code = codeObj.code;
      const qrUrl = `${baseUrl}/api/printer/qr-image/${encodeURIComponent(code)}?size=150`;
      
      for (let c = 0; c < copies; c++) {
        html += `
          <div class="label">
            <img src="${qrUrl}" class="qr-code" alt="QR: ${code}" crossorigin="anonymous" />
            <div class="code-text">${this._formatCodeForDisplay(code)}</div>
            ${codeObj.sortNo ? `<div class="sort-text">Sort: ${codeObj.sortNo}</div>` : ''}
          </div>
        `;
      }
    }
    
    return html;
  }

  _formatCodeForDisplay(code) {
    // Truncate long codes for display
    if (code.length > 20) {
      return code.substring(0, 8) + '...' + code.substring(code.length - 8);
    }
    return code;
  }
}

// ==========================================
// Export Singleton Instance
// ==========================================

export const PrintManager = new PrintManagerClass();

// Also export the class for advanced usage
export { PrintManagerClass };

// ==========================================
// Utility Functions
// ==========================================

/**
 * Check if browser printing is supported
 */
export const isBrowserPrintSupported = () => {
  return typeof window !== 'undefined' && typeof window.print === 'function';
};

/**
 * Get available print methods
 */
export const getAvailablePrintMethods = () => {
  const methods = [];
  
  methods.push({
    id: 'browser',
    name: 'Browser Print',
    description: 'Opens print dialog with formatted labels',
    available: isBrowserPrintSupported(),
    recommended: true
  });
  
  methods.push({
    id: 'pdf',
    name: 'PDF Download',
    description: 'Generates PDF file for printing',
    available: true,
    recommended: false
  });
  
  methods.push({
    id: 'raw',
    name: 'Raw Commands',
    description: 'Downloads .prn file for direct printer commands',
    available: true,
    recommended: false
  });
  
  return methods;
};

/**
 * Format code for label display
 */
export const formatLabelCode = (code, maxLength = 20) => {
  if (!code) return '';
  if (code.length <= maxLength) return code;
  return code.substring(0, 8) + '...' + code.substring(code.length - 8);
};

export default PrintManager;
